const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();
class ByteStringBase extends Uint8Array {
    static is(val) {
        return val instanceof Uint8Array && val.buffer instanceof ArrayBuffer;
    }
    static isOfLength(val, length) {
        return val.length === length && val.byteLength === length;
    }
    static of(...elements) {
        return Uint8Array.of(...elements);
    }
    static fromUtf8(input) {
        const output = textEncoder.encode(input);
        this.assertIs(output);
        return output;
    }
    static toUtf8(val) {
        return textDecoder.decode(val);
    }
    static fromHex(input) {
        const output = Uint8Array.fromHex(input);
        this.assertIs(output);
        return output;
    }
    static fromBase64(input) {
        const output = Uint8Array.fromBase64(input);
        this.assertIs(output);
        return output;
    }
    static fromBuffer(value) {
        const output = new Uint8Array(value);
        this.assertIs(output);
        return output;
    }
    static copy(val) {
        const output = new Uint8Array(val);
        this.assertIs(output);
        return output;
    }
    static compare(a, b) {
        for (let i = 0; i < a.length && i < b.length; i++) {
            if (a[i] !== b[i]) {
                return a[i] - b[i];
            }
        }
        return a.length - b.length;
    }
    static equals(a, b) {
        if (a === undefined || b === undefined) {
            return a === b;
        }
        if (a.length !== b.length) {
            return false;
        }
        return this.compare(a, b) === 0;
    }
    static isLessThanOrEqualTo(a, b) {
        return this.compare(a, b) <= 0;
    }
    static assertIs(val) {
        if (!this.is(val)) {
            throw new Error("Invalid val", {
                cause: val,
            });
        }
    }
}
export class ByteString extends ByteStringBase {
    static empty() {
        return new Uint8Array(0);
    }
    static concat(...parts) {
        const output = new Uint8Array(parts.reduce((previousValue, currentValue) => {
            return previousValue + currentValue.byteLength;
        }, 0));
        let cursor = 0;
        for (const part of parts) {
            output.set(part, cursor);
            cursor += part.byteLength;
        }
        return output;
    }
    static predecessor(val, minLength, maxLength) {
        if (val.length === 0) {
            return undefined;
        }
        for (let i = val.length - 1; i >= 0; i--) {
            if (val[i] === 0) {
                if (val.length <= minLength) {
                    continue;
                }
                else {
                    const output = val.slice(0, val.length - 1);
                    this.assertIs(output);
                    return output;
                }
            }
            else {
                const missingBytesToMaxLength = maxLength - i - 1;
                return this.of(...val.slice(0, i), val[i] - 1, ...Array(missingBytesToMaxLength).fill(0xff));
            }
        }
        return undefined;
    }
    static successor(val, maxLength) {
        if (maxLength === undefined || val.length < maxLength) {
            return this.of(...val, 0);
        }
        for (let i = val.length - 1; i >= 0; i--) {
            const byte = val[i];
            if (byte === 0xff) {
                continue;
            }
            return this.of(...val.slice(0, i), val[i] + 1, ...Array(Math.max(0, val.length - (i + 1))).fill(0x00));
        }
        return undefined;
    }
    static isSuccessor(a, b, maxLength) {
        return this.equals(a, this.successor(b, maxLength));
    }
}
export function FixedLengthByteString(length) {
    return class extends ByteStringBase {
        static LENGTH = length;
        static is(val) {
            return super.is(val) && this.isOfLength(val, length);
        }
        static assertIs(val) {
            if (!this.is(val)) {
                if (val.byteLength !== length) {
                    throw new Error("Invalid number of elements", {
                        cause: {
                            actual: val.byteLength,
                            expected: length,
                            val,
                        },
                    });
                }
                else {
                    throw new Error("Invalid val", {
                        cause: val,
                    });
                }
            }
        }
        static greatest() {
            return this.of(...Array(length).fill(0xff));
        }
        static least() {
            return this.of(...Array(length).fill(0x00));
        }
        static predecessor(val) {
            const output = ByteString.predecessor(val, length, length);
            if (output) {
                this.assertIs(output);
            }
            return output;
        }
        static successor(val) {
            const output = ByteString.successor(val, length);
            if (output) {
                this.assertIs(output);
            }
            return output;
        }
        static of(...elements) {
            const output = super.of(...elements);
            this.assertIs(output);
            return output;
        }
        static copy(val) {
            return super.copy(val);
        }
        static fromBuffer(input) {
            return super.fromBuffer(input);
        }
        static fromBase64(input) {
            return super.fromBase64(input);
        }
        static fromHex(input) {
            return super.fromHex(input);
        }
        static isSuccessor(a, b) {
            return ByteString.isSuccessor(a, b, length);
        }
        static isGreatest(val) {
            return this.equals(val, this.greatest());
        }
        static encode(val, consumer) {
            return consumer.write(val);
        }
        static async decode(producer) {
            return producer.read(length);
        }
        length;
        byteLength;
    };
}
//# sourceMappingURL=ByteString.js.map